import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import FileUploader from '@/components/FileUploader';
import { ACCEPTED_FILES_SURICATA, PONDURANCE_LABEL } from '@/constants/common';
import {
  rulesCreateDialogOpenAtom,
  useUploadSensorRule,
  validateFile
} from '@/pages/Administration/AdministrationRules';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import type RulesDialog from './RulesCreateDialog.types';

const DEFAULT_FORM_VALUES: RulesDialog = {
  attachments: [],
  file_type: '',
  allOrgsAcknowledged: false,
  key: undefined,
  sensor_rule_id: undefined
};

const RulesCreateDialog = () => {
  const [rulesCreateDialogOpen, setRulesCreateDialogOpen] = useAtom(
    rulesCreateDialogOpenAtom
  );
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);

  //-----------------------------------------------------------------
  const form = useForm<RulesDialog>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange'
  });
  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting }
  } = form;
  const [attachments, fileType] = useWatch({
    control,
    name: ['attachments', 'file_type']
  });
  const allOrgsAcknowledged = useWatch({
    control,
    name: 'allOrgsAcknowledged'
  });

  const handleClose = () => {
    form.reset();
    setRulesCreateDialogOpen(false);
    clearErrors();
  };
  const uploadSensorRule = useUploadSensorRule(handleClose);
  const onSubmit = handleSubmit((data: RulesDialog) => {
    if (!data.attachments?.[0] || !data.file_type) {
      return;
    }

    uploadSensorRule.mutate({
      file: data.attachments[0],
      file_type: data.file_type,
      key: data.key,
      sensor_rule_id: data.sensor_rule_id?.toString()
    });
  });

  const IS_UPLOAD_DISABLED =
    isSubmitting ||
    !!errors.attachments ||
    !attachments?.[0] ||
    !fileType ||
    (selectedOrganization.id === 0 && !allOrgsAcknowledged);

  //-----------------------------------------------------------------
  useEffect(() => {
    if (selectedOrganization.id === 0) {
      setValue('allOrgsAcknowledged', false);
    } else {
      setValue('allOrgsAcknowledged', true);
    }
  }, [selectedOrganization.id, setValue]);

  useEffect(() => {
    const currentFile = attachments?.[0];
    validateFile(
      currentFile,
      fileType,
      selectedOrganization.id,
      setError,
      clearErrors
    );
  }, [attachments, clearErrors, fileType, setError, selectedOrganization]);

  return (
    <Dialog
      open={rulesCreateDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Upload Rule or Configuration File</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <DialogContent>
            {selectedOrganization.label === PONDURANCE_LABEL && (
              <Alert severity="warning">
                Files cannot be uploaded to Pondurance Internal. Please select a
                different organization.
              </Alert>
            )}
            {selectedOrganization.label !== PONDURANCE_LABEL && (
              <>
                <Typography sx={{ my: 1 }} color="textSecondary">
                  Organization
                </Typography>
                <FormControl fullWidth sx={{ px: 1, mb: 1 }}>
                  <Typography sx={{ fontWeight: 'normal' }} variant="h5">
                    {selectedOrganization.name}
                  </Typography>
                  {selectedOrganization.id === 0 && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!allOrgsAcknowledged}
                          onChange={(e) => {
                            setValue('allOrgsAcknowledged', e.target.checked, {
                              shouldValidate: true
                            });
                          }}
                        />
                      }
                      label={
                        <Typography color="error">
                          I acknowledge this is a global change that will apply
                          to all organizations
                        </Typography>
                      }
                    />
                  )}
                </FormControl>
                <Typography color="textSecondary">File Type</Typography>
                <FormControl fullWidth sx={{ px: 2 }}>
                  <RadioGroup
                    value={fileType}
                    onChange={(e) => {
                      setValue(
                        'file_type',
                        e.target.value as RulesDialog['file_type']
                      );
                      if (errors.attachments?.type === 'fileType') {
                        clearErrors('attachments');
                      }
                    }}
                  >
                    <FormControlLabel
                      value="rule"
                      control={<Radio />}
                      label="Rule"
                      sx={{ alignItems: 'flex-start', py: 0.75 }}
                    />
                    <FormControlLabel
                      value="configuration"
                      control={<Radio />}
                      label="Configuration"
                      sx={{ mb: 1.5 }}
                    />
                  </RadioGroup>
                </FormControl>
                <Typography sx={{ my: 1 }} color="textSecondary">
                  File
                </Typography>
                <FormControl fullWidth sx={{ px: 1 }}>
                  <FileUploader
                    singleFile
                    titleButton="button"
                    titleButtonVariant="outlined"
                    titleButtonTooltip=".config and .rules files are supported"
                    errorMessage={errors.attachments?.message}
                    isRequired
                    acceptedTypes={ACCEPTED_FILES_SURICATA}
                    skipCheckFiles
                  />
                </FormControl>
                <br />
                {(errors.attachments?.message || errors.root?.message) && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {errors.attachments?.message?.split('\n').map((line) => (
                      <React.Fragment key={line}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'end' }}>
            <div>
              <Button onClick={handleClose} sx={{ mr: 2 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={IS_UPLOAD_DISABLED}
                type="submit"
                sx={{ bgcolor: 'primary.main' }}
              >
                Upload File
              </Button>
            </div>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default RulesCreateDialog;
